import React, { FC, memo, useState, useCallback, useEffect } from 'react';
import { Field, useField } from 'formik';
import { Container, Input, Row, Select } from './styles';
import { Props } from './types';

const typeOptions = [
  {
    label: 'Single Select',
    value: 'single',
  },
  {
    label: 'Multi Select',
    value: 'multi',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
];

const selectionOptions = [
  {
    label: 'Required',
    value: 'required',
  },
  {
    label: 'Optional',
    value: 'optional',
  },
];

const Details: FC<Props> = ({ maxItems, isProcessing, ...props }) => {
  const [{ value: minimumValue }, , { setValue: setMinimumValue }] =
    useField('choices.minimum');
  const [{ value: maximumValue }, , { setValue: setMaximumValue }] =
    useField('choices.maximum');

  const [type, setType] = useState<string>('single');
  const [selection, setSelection] = useState<string>('optional');

  const handleChangeType = useCallback(
    (event: React.ChangeEvent<{ label?: string; value: unknown }>) => {
      const value = event.target.value as string;
      setType(value);
      switch (value) {
        case 'single': {
          setMaximumValue(1);
          if (minimumValue > 1) {
            setMinimumValue(1);
          }
          break;
        }
        case 'multi': {
          setMaximumValue(maxItems);
          if (minimumValue > 1) {
            setMinimumValue(1);
          }
          break;
        }
        case 'custom':
        default:
          break;
      }
    },
    [setType, setMaximumValue, minimumValue, setMinimumValue, maxItems],
  );

  const handleChangeSelection = useCallback(
    (event: React.ChangeEvent<{ label?: string; value: unknown }>) => {
      const value = event.target.value as string;
      setSelection(value);
      switch (value) {
        case 'optional': {
          setMinimumValue(0);
          break;
        }
        case 'required': {
          setMinimumValue(1);
          break;
        }
        default:
          break;
      }
    },
    [setSelection, setMinimumValue],
  );

  useEffect(() => {
    if (maximumValue === 1) {
      setType('single');
    } else if (maximumValue === maxItems) {
      setType('multi');
    }

    if ((maximumValue > 1 && maximumValue < maxItems) || minimumValue > 1) {
      setType('custom');
    }

    if (minimumValue === 0) {
      setSelection('optional');
    } else {
      setSelection('required');
    }
  }, [maximumValue, minimumValue, setType, setSelection, maxItems]);

  return (
    <Container {...props}>
      <Field
        name="name"
        as={Input}
        label="Modifier name"
        disabled={isProcessing}
      />
      <Row style={{ marginTop: '2rem' }}>
        <Select
          label="Selection"
          options={selectionOptions}
          placeholder="Select selection"
          value={selection}
          handleChange={handleChangeSelection}
          disabled={type === 'custom' || isProcessing}
        />
        <Select
          label="Type"
          options={typeOptions}
          placeholder="Select type"
          value={type}
          disabled={isProcessing}
          handleChange={handleChangeType}
        />
      </Row>
      <Row>
        <Field
          name="choices.minimum"
          as={Input}
          label="Minimum"
          type="number"
          min="0"
          max={maxItems}
          step="1"
          placeholder="Select amount"
          disabled={type !== 'custom' || isProcessing}
        />
        <Field
          name="choices.maximum"
          as={Input}
          label="Maximum"
          type="number"
          min="1"
          max={maxItems}
          step="1"
          placeholder="Select amount"
          disabled={type !== 'custom' || isProcessing}
        />
      </Row>
    </Container>
  );
};

export default memo(Details);
